import React, { useContext } from "react";
import { Formik, FormikHelpers } from "formik";
import { Form, Button } from "react-bootstrap";
import SimpleLoader from "lib/components/common/simple-loader";
import { USER_ROLES } from "lib/constants";
import { getArrayFrom, getPropsAsArrayFromArray } from "lib/utils/array-utils";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import RequiredField from "lib/components/common/required-field";
import { AppContext } from "lib/contexts/app-context";

interface EditUserFormProps {
  initialData: ClinicUser;
  updateUser: (data: CreateUserRequest) => Promise<void>;
  closeModal: () => void;
  onPasswordReset: () => void;
  isLoading: boolean;
  clinics: Clinic[];
}

const EditUserForm: React.FC<EditUserFormProps> = ({
  initialData,
  updateUser,
  closeModal,
  onPasswordReset,
  isLoading,
  clinics,
}) => {
  const { appState } = useContext(AppContext);
  const isReceptionist = appState?.userRole === "receptionist";

  const handleSubmit = async (
    values: ClinicUser,
    { setSubmitting }: FormikHelpers<ClinicUser>
  ) => {
    console.log("Form Values:", values);
    await updateUser(values);
    closeModal();
    setSubmitting(false);
  };

  return (
    <>
      <Formik initialValues={initialData} onSubmit={handleSubmit}>
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched,
        }) =>
          isLoading ? (
            <SimpleLoader />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="form-group">
                <Form.Label>
                  First Name <RequiredField />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isReceptionist}
                  isInvalid={touched.firstName && !!errors.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>
                  Last Name <RequiredField />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  disabled={isReceptionist}
                  onBlur={handleBlur}
                  isInvalid={touched.lastName && !!errors.lastName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>
                  Email <RequiredField />
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={values.email}
                  disabled={isReceptionist}
                  readOnly
                />
              </Form.Group>
              <Form.Group className="form-group">
                <Form.Label>
                  Role <RequiredField />
                </Form.Label>
                <Form.Control
                  as="select"
                  name="role"
                  value={values.role}
                  onChange={handleChange}
                  disabled={isReceptionist}
                >
                  {getArrayFrom<string>(USER_ROLES).map((role) => (
                    <option
                      key={role}
                      value={role}
                      // selected={role === initialData.role}
                    >
                      {role.toUpperCase()}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <SimpleDropdown
                label="Clinic"
                id="clinicIds"
                multiple
                options={getPropsAsArrayFromArray<Clinic, "name">(
                  clinics,
                  "name"
                )}
                values={getPropsAsArrayFromArray<Clinic, "id">(clinics, "id")}
                selectedValue={values.clinicIds}
                disabled={isReceptionist}
                onChange={handleChange}
                required
              />

              {!isReceptionist && (
                <div className="d-flex justify-content-between">
                  <Button variant="secondary" onClick={onPasswordReset}>
                    Reset Password
                  </Button>

                  <Button type="submit" className="btn btn-primary px-4">
                    Save Changes
                  </Button>
                </div>
              )}
            </Form>
          )
        }
      </Formik>
    </>
  );
};

export default EditUserForm;
